"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Dialog, DialogContent, DialogTitle } from "@/shadcn/ui/dialog";
import { cn } from "@/lib/utils";
import {
  Asset,
  Catalog,
  FundedCol,
  PlatformId,
  PRODUCT_ORDER,
  RapidFundedVariants,
  ProductId,
  SizeEntry,
  SizeId,
  VariantId,
} from "@/lib/new-pricing/products";
import type { ResolvedPricing } from "@/lib/new-pricing/fromSanity";
import type { NewPricingCopy } from "./copy";
import { fmtUsd } from "./PlanTable";

/**
 * Compare dialog per Figma 6216:5162 (picker) + 6183:2586 (results):
 * tick up to 4 plans — each purchasable variant is its own chip (Futures Core
 * offers both 50k challenges: MPL and DLL) — and the table below shows the
 * whole journey per plan, with rows that differ highlighted. Selection
 * survives switching the asset toggle, so plans can be compared across assets.
 */

const MAX_PLANS = 4;

type PlanRef = {
  asset: Asset;
  product: ProductId;
  size: SizeId;
  /** the challenge variant for core/rapid, the funded variant for instant */
  variant: VariantId;
  /** CFD only: the platform the plan is bought for — values may diverge per
   *  platform in the future. */
  platform?: PlatformId;
};

const planKey = (p: PlanRef) => `${p.asset}:${p.product}:${p.size}:${p.variant}:${p.platform ?? ""}`;

const SIZE_LABEL: Record<SizeId, string> = { "50k": "$50k", "100k": "$100k", "200k": "$200k" };

const CFD_PLATFORM_LABEL: Partial<Record<PlatformId, string>> = {
  mt5: "MT5",
  volumetrica: "Volumetrica",
};

/** Chip secondary text: the platform (family) the plan belongs to (per Figma). */
function chipMeta(asset: Asset, product: ProductId, entry: SizeEntry, variant: VariantId, platform?: PlatformId) {
  if (asset === "futures") {
    if (product === "rapid") return { platform: "Fast Track", rule: null };
    // MPL is dxFeed-only; DLL is sold on BOTH dxFeed and Tradovate.
    return variant === "mpl" ? { platform: "dxFeed", rule: "MPL" } : { platform: "dxFeed · Tradovate", rule: "DLL" };
  }
  return { platform: (platform && CFD_PLATFORM_LABEL[platform]) ?? "MT5 · Vol", rule: null };
}

/** All selectable plans of one product for the given asset. */
function productPlans(catalog: Catalog, asset: Asset, product: ProductId): { ref: PlanRef; entry: SizeEntry }[] {
  const plans: { ref: PlanRef; entry: SizeEntry }[] = [];
  for (const entry of catalog[asset][product].sizes) {
    const cols = product === "instant" ? entry.funded : entry.challenge;
    const seen = new Set<VariantId>();
    for (const col of cols) {
      // Rapid: one chip per size (its funded Classic/Daily duality is merged in the table)
      const variant: VariantId = product === "rapid" ? "single" : col.variant;
      if (seen.has(variant)) continue;
      seen.add(variant);
      if (asset === "cfd_crypto") {
        // One chip per platform: shows where each plan is available and keeps
        // room for per-platform values later.
        for (const platform of entry.platforms) {
          plans.push({ ref: { asset, product, size: entry.size, variant, platform }, entry });
        }
      } else {
        plans.push({ ref: { asset, product, size: entry.size, variant }, entry });
      }
    }
  }
  return plans;
}

/** Merge a value across Rapid's two funded variants, e.g. "3 days / 1 day". */
function mergeFunded(cols: FundedCol[], pick: (f: FundedCol) => string): string {
  const values = cols.map(pick);
  return [...new Set(values)].join(" / ");
}

type Column = {
  key: string;
  ref: PlanRef;
  title: string;
  subtitle: string;
  presel: string;
  priceUsd?: number;
  ctaLabel: (c: NewPricingCopy) => string;
  values: Record<string, string>;
  /** marketing-added rows: `custom:<id>` → label (values live in `values`) */
  customLabels: Record<string, string>;
};

function buildColumn(
  catalog: Catalog,
  rapidFundedVariants: RapidFundedVariants,
  ref: PlanRef,
  copy: NewPricingCopy,
): Column | null {
  const c = copy;
  const product = catalog[ref.asset][ref.product];
  const entry = product.sizes.find((s) => s.size === ref.size);
  if (!entry) return null;

  const challengeCol =
    ref.product === "instant"
      ? undefined
      : (entry.challenge.find((col) => col.variant === ref.variant) ?? entry.challenge[0]);
  const fundedCols =
    ref.product === "rapid"
      ? entry.funded.filter((col) => rapidFundedVariants === "both" || col.variant === rapidFundedVariants)
      : [entry.funded.find((col) => col.variant === ref.variant) ?? entry.funded[0]].filter((x): x is FundedCol => !!x);
  if (!challengeCol && fundedCols.length === 0) return null;

  const priceUsd = challengeCol?.data.priceUsd ?? fundedCols[0]?.priceUsd;
  const presel = challengeCol?.presel ?? fundedCols[0]?.presel ?? "";

  const marketLabel = ref.asset === "futures" ? c.markets.futures : c.markets.cfdCrypto;
  const meta = chipMeta(ref.asset, ref.product, entry, ref.variant, ref.platform);
  const platformLabel = ref.platform ? CFD_PLATFORM_LABEL[ref.platform] : undefined;
  const stageLabel = ref.product === "instant" ? c.stage.instantOnly : c.compare.challenge;

  // literal doc semantics: blank numbers render "—"
  const usd = (n: number | undefined) => (n == null ? c.values.dash : fmtUsd(n));
  const consistency = (v: number | null) => (v == null ? c.values.none : `${v}%`);
  const chCons = challengeCol ? consistency(challengeCol.data.consistencyPct) : null;
  const fuCons = mergeFunded(fundedCols, (f) => consistency(f.data.consistencyPct));

  // Marketing-added rows: challenge + funded specs merged per id ("/" joined
  // across Rapid's funded variants), so compare stays in sync with the cards.
  const customLabels: Record<string, string> = {};
  const customValues: Record<string, string[]> = {};
  const collect = (specs?: { id: string; label: string; value: string }[]) => {
    for (const sp of specs ?? []) {
      const key = `custom:${sp.id}`;
      customLabels[key] = customLabels[key] || sp.label;
      (customValues[key] ??= []).push(sp.value);
    }
  };
  collect(challengeCol?.customSpecs);
  for (const f of fundedCols) collect(f.customSpecs);
  const customEntries = Object.fromEntries(
    Object.entries(customValues).map(([k, vs]) => [k, [...new Set(vs)].join(" / ")]),
  );

  return {
    key: planKey(ref),
    ref,
    title: `${c.products[ref.product].name} ${SIZE_LABEL[ref.size]}`,
    subtitle: `${stageLabel} · ${marketLabel}${platformLabel ? ` · ${platformLabel}` : ""}${meta.rule ? ` · ${meta.rule}` : ""}`,
    presel,
    priceUsd,
    ctaLabel: (cc) => (ref.product === "instant" ? cc.cta.getFunded : cc.cta.startChallenge),
    values: {
      oneTimeFee: "", // rendered specially (strike + discount pill)
      profitTarget: challengeCol ? usd(challengeCol.data.profitTargetUsd) : c.values.dash,
      maxDrawdown: challengeCol
        ? usd(challengeCol.data.maxDrawdownUsd)
        : mergeFunded(fundedCols, (f) => usd(f.data.maxDrawdownUsd)),
      drawdownMode: c.values.drawdownMode,
      maxPositionLoss: challengeCol
        ? challengeCol.data.maxPositionLossPct != null
          ? `${challengeCol.data.maxPositionLossPct}% *`
          : c.values.dash
        : mergeFunded(fundedCols, (f) =>
            f.data.maxPositionLossPct != null ? `${f.data.maxPositionLossPct}% *` : c.values.dash,
          ),
      dailyLossLimit: challengeCol
        ? challengeCol.data.dailyLossLimitUsd != null
          ? `${fmtUsd(challengeCol.data.dailyLossLimitUsd)} *`
          : c.values.dash
        : mergeFunded(fundedCols, (f) =>
            f.data.dailyLossLimitUsd != null ? `${fmtUsd(f.data.dailyLossLimitUsd)} *` : c.values.dash,
          ),
      consistency: chCons != null && chCons !== fuCons ? `${chCons} / ${fuCons}` : fuCons,
      profitSplit: mergeFunded(fundedCols, (f) =>
        f.data.profitSplitTrader == null ? c.values.dash : `${f.data.profitSplitTrader} / ${100 - f.data.profitSplitTrader}`,
      ),
      payoutEvery: mergeFunded(fundedCols, (f) =>
        f.data.payoutEveryDays == null ? c.values.dash : c.values.payoutDays(f.data.payoutEveryDays),
      ),
      duration:
        challengeCol?.data.durationDays != null ? c.values.days(challengeCol.data.durationDays) : c.values.dash,
      resetFee: mergeFunded(fundedCols, (f) =>
        f.data.resetFeeUsd == null ? c.values.dash : fmtUsd(f.data.resetFeeUsd),
      ),
      maxAccounts: product.maxAccounts == null ? c.values.dash : String(product.maxAccounts),
      ...customEntries,
    },
    customLabels,
  };
}

const ROW_ORDER: { key: string; label: (c: NewPricingCopy) => string }[] = [
  { key: "oneTimeFee", label: (c) => c.compare.oneTimeFee },
  { key: "profitTarget", label: (c) => c.kpiLabels.profitTarget },
  { key: "maxDrawdown", label: (c) => c.kpiLabels.maxDrawdown },
  { key: "drawdownMode", label: (c) => c.kpiLabels.drawdownMode },
  { key: "maxPositionLoss", label: (c) => c.kpiLabels.maxPositionLoss },
  { key: "dailyLossLimit", label: (c) => c.kpiLabels.dailyLossLimit },
  { key: "consistency", label: (c) => c.kpiLabels.consistency },
  { key: "profitSplit", label: (c) => c.kpiLabels.profitSplit },
  { key: "payoutEvery", label: (c) => c.kpiLabels.payoutEvery },
  { key: "duration", label: (c) => c.kpiLabels.duration },
  { key: "resetFee", label: (c) => c.kpiLabels.resetFee },
  { key: "maxAccounts", label: (c) => c.kpiLabels.maxAccounts },
];

const ROW_H = "h-[5.5rem]";
const CELL_TEXT = "font-geist text-[2rem] leading-[1.5] font-medium whitespace-nowrap text-black/80";
/* Fixed band heights shared by real columns, ghost columns and the label
   column spacer — alignment holds by construction, whatever the copy length. */
const CARD_H = "h-[12rem]";
const CTA_H = "h-[7.375rem]";
// label spacer = column py-top (0.75) + card height (12) + column gap (1.375)
const LABEL_PT = "pt-[14.125rem]";

export default function CompareDialog({
  open,
  onOpenChange,
  initialAsset,
  fixedAsset,
  copy,
  resolved,
  onCheckout,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initialAsset: Asset;
  /** Asset-locked pages: only this asset's plans are offered (toggle hidden). */
  fixedAsset?: Asset;
  copy: NewPricingCopy;
  resolved: ResolvedPricing;
  onCheckout?: (presel: string, ref: PlanRef) => void;
}) {
  const [asset, setAsset] = useState<Asset>(fixedAsset ?? initialAsset);
  const [selected, setSelected] = useState<PlanRef[]>([]);

  // Each open starts on the asset the page is currently showing; the ticked
  // plans survive (selection works across assets).
  useEffect(() => {
    if (open) setAsset(fixedAsset ?? initialAsset);
  }, [open, initialAsset, fixedAsset]);

  const toggle = (ref: PlanRef) => {
    setSelected((prev) => {
      const key = planKey(ref);
      const without = prev.filter((p) => planKey(p) !== key);
      if (without.length < prev.length) return without;
      if (prev.length >= MAX_PLANS) return prev;
      return [...prev, ref];
    });
  };

  const columns = useMemo(
    () =>
      selected
        .map((ref) => buildColumn(resolved.catalog, resolved.rapidFundedVariants, ref, copy))
        .filter((col): col is Column => !!col),
    [selected, copy],
  );

  // Standard rows + marketing-added rows (customSpecs), paired across the
  // ticked plans — the compare view always mirrors the cards.
  const allRows = useMemo(() => {
    const customs = new Map<string, string>();
    for (const col of columns)
      for (const [key, label] of Object.entries(col.customLabels))
        if (!customs.has(key)) customs.set(key, label);
    return [
      ...ROW_ORDER,
      ...[...customs].map(([key, label]) => ({ key, label: () => label })),
    ];
  }, [columns]);

  // Rows where every selected plan is "—" say nothing — drop them (e.g. the
  // Daily Loss Limit row when only CFD Core / Instant are ticked, or the
  // challenge rows when only Instant plans are compared).
  const visibleRows = useMemo(
    () =>
      allRows.filter(
        (row) =>
          row.key === "oneTimeFee" ||
          columns.some((col) => (col.values[row.key] ?? copy.values.dash) !== copy.values.dash),
      ),
    [allRows, columns, copy],
  );

  const differing = useMemo(() => {
    const set = new Set<string>();
    if (columns.length < 2) return set;
    for (const row of allRows) {
      const values =
        row.key === "oneTimeFee"
          ? columns.map((col) => String(col.priceUsd))
          : columns.map((col) => col.values[row.key] ?? copy.values.dash);
      if (new Set(values).size > 1) set.add(row.key);
    }
    return set;
  }, [allRows, columns, copy]);

  const isSelected = (ref: PlanRef) => selected.some((p) => planKey(p) === planKey(ref));
  const selectionFull = selected.length >= MAX_PLANS;

  const legends = (product: ProductId) => {
    if (asset !== "futures" || product === "rapid") return null;
    return (
      <span className="rounded-[0.5rem] border border-[#103037]/15 bg-white px-[1.5rem] py-[0.5rem] font-geist text-[2rem] leading-[1.3] tracking-[-0.05em] whitespace-nowrap text-[#103037]">
        {copy.compare.legendMpl.replace("*", "")} · {copy.compare.legendDll.replace("*", "")}
      </span>
    );
  };

  const emptySlots = Math.max(0, MAX_PLANS - columns.length);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overscroll-contain bg-white p-0",
          // mobile: full screen (Figma 6216:5486); the DIALOG is the one
          // scroll container — both axes — so chips and table pan with the page
          "max-lg:!top-0 max-lg:!left-0 max-lg:h-[100dvh] max-lg:max-h-[100dvh] max-lg:!w-full max-lg:!translate-x-0 max-lg:!translate-y-0 max-lg:overflow-auto max-lg:rounded-none",
          // desktop: centered card, vertical scroll only
          "lg:max-h-[90dvh] lg:!w-[min(166rem,calc(100%-2rem))] lg:overflow-y-auto lg:rounded-[1.5rem]",
        )}>
        <div className="flex flex-col p-[2.5rem] pt-[3rem] max-lg:w-max max-lg:min-w-full lg:p-[5rem]">
          {/* header */}
          <div className="flex flex-col gap-[1rem]">
            <DialogTitle className="font-geist text-[3.25rem] leading-[1.3] font-medium text-black">
              {copy.compare.title}
            </DialogTitle>
            <p className="font-geist text-[2.25rem] leading-[1.3] text-black/50">{copy.compare.sub}</p>
          </div>

          {/* asset toggle — left aligned (client feedback); hidden when the
              page is locked to one asset */}
          {!fixedAsset && (
          <div className="mt-[4rem] flex items-center gap-[0.75rem] lg:mt-[5rem]">
            {(["futures", "cfd_crypto"] as Asset[]).map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={asset === a}
                onClick={() => setAsset(a)}
                className={cn(
                  "rounded-[0.75rem] px-[3rem] py-[2rem] font-geist text-[2.5rem] leading-[1.3] font-medium whitespace-nowrap text-black transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a] lg:w-[31.75rem]",
                  asset === a ? "border border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
                )}>
                {a === "futures" ? copy.markets.futures : copy.markets.cfdCrypto}
              </button>
            ))}
          </div>
          )}

          {/* product groups with plan chips */}
          <div className="mt-[4.75rem] flex flex-col gap-[2.75rem]">
            {PRODUCT_ORDER.map((product, idx) => {
              const plans = productPlans(resolved.catalog, asset, product);
              const pc = copy.products[product];
              return (
                <div
                  key={product}
                  className={cn(
                    "flex flex-col gap-[1.5rem]",
                    idx > 0 && "border-t border-dashed border-black/15 pt-[2.75rem]",
                  )}>
                  <div className="flex flex-wrap items-center gap-[1.5rem]">
                    <span className="font-geist text-[3rem] leading-[1.3] font-medium text-black">{pc.name}</span>
                    <span className="rounded-[0.5rem] bg-[#1aa87f] px-[1.5rem] py-[0.5rem] font-geist text-[2rem] leading-[1.3] tracking-[-0.05em] whitespace-nowrap text-white">
                      {pc.tag}
                    </span>
                    {legends(product)}
                  </div>
                  <div className="flex items-center gap-[0.75rem] lg:flex-wrap">
                    {plans.map(({ ref, entry }) => {
                      const meta = chipMeta(asset, product, entry, ref.variant, ref.platform);
                      const active = isSelected(ref);
                      const disabled = !active && selectionFull;
                      return (
                        <button
                          key={planKey(ref)}
                          type="button"
                          role="checkbox"
                          aria-checked={active}
                          disabled={disabled}
                          onClick={() => toggle(ref)}
                          className={cn(
                            "flex shrink-0 items-center gap-[1.25rem] rounded-[0.75rem] px-[3rem] py-[2rem] whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
                            active ? "border border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
                            disabled && "cursor-not-allowed opacity-40 hover:bg-[#f1f1f1]",
                          )}>
                          <span className="font-geist text-[2.5rem] leading-[1.3] font-medium text-black">
                            {SIZE_LABEL[ref.size]}
                          </span>
                          <span className="font-geist text-[2.25rem] leading-[1.3] text-black/50">{meta.platform}</span>
                          {meta.rule && (
                            <>
                              <span
                                className="h-[2.625rem] w-px bg-black/15"
                                aria-hidden="true"
                              />
                              <span className="font-geist text-[2.25rem] leading-[1.3] text-black/50">{meta.rule}</span>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* results table — only once at least one plan is ticked (Figma:
              6216:5162 is the picker-only empty state, 6183:2586 the results) */}
          {columns.length > 0 && (
            <>
              <p className="mt-[4rem] font-geist text-[1.625rem] text-black/40 lg:hidden">{copy.compare.mobileHint}</p>
              <div className="mt-[2rem] lg:overflow-x-auto">
                <div className="flex min-w-[150rem] items-stretch rounded-[0.5rem]">
                  {/* label column */}
                  <div
                    className={cn(
                      "z-10 flex w-[30.875rem] shrink-0 flex-col rounded-l-[0.5rem] bg-[#f4f4f4] lg:sticky lg:left-0",
                      LABEL_PT,
                    )}>
                    {visibleRows.map((row, i) => (
                      <div
                        key={row.key}
                        className={cn(
                          ROW_H,
                          "flex items-center border-b border-black/10 px-[2rem]",
                          i === 0 && "border-t",
                        )}>
                        <span className="truncate font-geist text-[2rem] leading-[1.3] whitespace-nowrap text-black/90">
                          {row.label(copy)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* plan columns */}
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      className="flex min-w-0 flex-1 flex-col gap-[1.375rem] bg-[#f4f4f4] px-[1.125rem] py-[0.75rem] last:rounded-r-[0.5rem]">
                      {/* plan card */}
                      <div
                        className={cn(
                          CARD_H,
                          "flex flex-col justify-center gap-[1rem] rounded-[0.5rem] border border-black/10 bg-white p-[2rem]",
                        )}>
                        <div className="flex min-w-0 items-center justify-between gap-[1rem]">
                          <span className="truncate font-geist text-[2.75rem] leading-[1.2] font-medium tracking-[-0.03em] whitespace-nowrap text-black">
                            {col.title}
                          </span>
                          <button
                            type="button"
                            aria-label={`${col.title} ×`}
                            onClick={() => toggle(col.ref)}
                            className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full text-black/50 transition-colors hover:bg-black/5 hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]">
                            <svg
                              viewBox="0 0 16 16"
                              fill="none"
                              className="h-[2.25rem] w-[2.25rem]"
                              aria-hidden="true">
                              <path
                                d="M4 4l8 8M12 4l-8 8"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>
                        <span className="truncate font-geist text-[2rem] leading-[1.3] whitespace-nowrap text-black/60">
                          {col.subtitle}
                        </span>
                      </div>

                      {/* cells */}
                      <div className="flex flex-col">
                        {visibleRows.map((row, i) => {
                          const diff = differing.has(row.key);
                          return (
                            <div
                              key={row.key}
                              className={cn(
                                ROW_H,
                                "flex items-center border-b border-black/10 bg-white px-[3rem]",
                                i === 0 && "border-t",
                                diff && "bg-[#e2f3ef]",
                              )}>
                              {row.key === "oneTimeFee" ? (
                                col.priceUsd == null ? (
                                  <span className={cn(CELL_TEXT, "font-normal text-black/40")}>{copy.values.dash}</span>
                                ) : (
                                <span className="flex min-w-0 items-center gap-[1rem] whitespace-nowrap">
                                  <span className="font-geist text-[1.75rem] leading-[1.35] font-medium tracking-[-0.03em] text-black/40 line-through">
                                    {fmtUsd(col.priceUsd)}
                                  </span>
                                  <span className={CELL_TEXT}>{fmtUsd(resolved.discounted(col.priceUsd))}</span>
                                  <span className="rounded-full bg-[#1aa87f] px-[1rem] py-[0.5rem] font-geist text-[1.75rem] leading-[1.2] font-medium text-white">
                                    -{resolved.discountPercent}%
                                  </span>
                                </span>
                                )
                              ) : (
                                <span
                                  className={cn(
                                    CELL_TEXT,
                                    "truncate",
                                    (col.values[row.key] ?? copy.values.dash) === copy.values.dash &&
                                      "font-normal text-black/40",
                                  )}>
                                  {col.values[row.key] ?? copy.values.dash}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <Link
                        href={resolved.checkoutUrl(col.presel)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onCheckout?.(col.presel, col.ref)}
                        className={cn(
                          CTA_H,
                          "flex items-center justify-center rounded-[0.25rem] border border-black/10 bg-black px-[3rem] font-geist text-[2rem] leading-[1.35] font-semibold tracking-[-0.02em] whitespace-nowrap text-white capitalize transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
                        )}>
                        {col.ctaLabel(copy)}
                      </Link>
                    </div>
                  ))}

                  {/* ghost placeholder columns keep the 4-slot layout (per Figma) */}
                  {Array.from({ length: emptySlots }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="flex min-w-0 flex-1 flex-col gap-[1.375rem] bg-[#f4f4f4] px-[1.125rem] py-[0.75rem] last:rounded-r-[0.5rem]">
                      <div
                        className={cn(
                          CARD_H,
                          "flex items-center justify-center rounded-[0.5rem] border border-dashed border-black/15 bg-white/60 p-[2rem]",
                        )}>
                        <span className="truncate font-geist text-[2rem] leading-[1.3] whitespace-nowrap text-black/35">
                          {copy.compare.emptySlot}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        {visibleRows.map((row, j) => (
                          <div
                            key={row.key}
                            className={cn(
                              ROW_H,
                              "flex items-center border-b border-black/10 bg-white px-[3rem]",
                              j === 0 && "border-t",
                            )}>
                            <span className="font-geist text-[1.75rem] text-black/30">-</span>
                          </div>
                        ))}
                      </div>
                      <div className={CTA_H} />
                    </div>
                  ))}
                </div>
              </div>

              {/* breach footnotes */}
              <div className="flex flex-col gap-[0.5rem] pt-[2.5rem]">
                <p className="font-geist text-[1.75rem] leading-[1.4] text-black/45">{copy.footnotes.mpl}</p>
                <p className="font-geist text-[1.75rem] leading-[1.4] text-black/45">{copy.footnotes.dll}</p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
